import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "executionTab", "completionTab", "errorTab", "performanceTab", "notificationsTab",
    "storyTimeout", "pauseBetween", "thresholdValue", "patternList",
    "avgTimePreview", "queueTimePreview", "reliabilityPreview"
  ]

  connect() {
    this.buildSessionId = this.element.dataset.buildSessionId
    this.settings = this.loadSettings()
    this.selectedPreset = 'custom'
    this.updatePreview()
  }

  loadSettings() {
    const stored = localStorage.getItem(`automation_settings_${this.buildSessionId}`)
    if (stored) {
      return JSON.parse(stored)
    }
    return this.defaultSettings()
  }

  defaultSettings() {
    return {
      execution: {
        story_timeout: 30,
        pause_between_stories: 2,
        optimize_queue: true,
        parallel_execution_limit: 1,
        max_retries: 2,
        retry_delay: 5,
        continue_on_failure: false
      },
      completion_detection: {
        threshold: 0.6,
        idle_timeout: 10,
        multiple_signals: true,
        signal_types: {
          commands: true,
          messages: true,
          files: true,
          tests: true,
          explicit_markers: true
        },
        custom_patterns: [],
        detection_window: 60
      },
      error_handling: {
        strategy: 'retry',
        max_retries: 2,
        notify_on_error: true,
        notify_on_completion: true,
        checkpoint_frequency: 5,
        auto_recovery: true,
        error_threshold: 3
      },
      performance: {
        enable_monitoring: true,
        metric_collection_interval: 30,
        performance_alerts: true,
        resource_limits: {
          max_memory_mb: 1024,
          max_cpu_percent: 80,
          max_execution_time_minutes: 120
        }
      },
      notifications: {
        channels: {
          email: false,
          in_app: true,
          slack: false,
          webhook: false
        },
        events: {
          automation_start: true,
          automation_complete: true,
          story_complete: false,
          error_occurred: true,
          performance_warning: true
        }
      }
    }
  }

  switchTab(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab
    
    // Update tab buttons
    this.element.querySelectorAll('.tab').forEach(t => {
      t.classList.remove('tab-active')
    })
    event.currentTarget.classList.add('tab-active')
    
    // Show/hide content
    this.element.querySelectorAll('.tab-content').forEach(content => {
      content.classList.add('hidden')
    })
    
    const targetMap = {
      'execution': this.executionTabTarget,
      'completion': this.completionTabTarget,
      'error-handling': this.errorTabTarget,
      'performance': this.performanceTabTarget,
      'notifications': this.notificationsTabTarget
    }
    
    if (targetMap[tab]) {
      targetMap[tab].classList.remove('hidden')
    }
  }

  selectPreset(event) {
    const preset = event.currentTarget.dataset.preset
    this.selectedPreset = preset
    
    // Update visual selection
    this.element.querySelectorAll('[data-action*="selectPreset"]').forEach(card => {
      card.classList.remove('bg-primary', 'text-primary-content')
      card.classList.add('bg-base-200')
    })
    event.currentTarget.classList.remove('bg-base-200')
    event.currentTarget.classList.add('bg-primary', 'text-primary-content')
    
    // Load preset settings
    if (preset !== 'custom') {
      this.loadPresetSettings(preset)
    }
  }

  loadPresetSettings(preset) {
    const presets = {
      conservative: {
        execution: { story_timeout: 60, pause_between_stories: 5 },
        completion_detection: { threshold: 0.8, idle_timeout: 20 },
        error_handling: { strategy: 'stop', max_retries: 1 }
      },
      balanced: {
        execution: { story_timeout: 30, pause_between_stories: 2 },
        completion_detection: { threshold: 0.6, idle_timeout: 10 },
        error_handling: { strategy: 'retry', max_retries: 2 }
      },
      aggressive: {
        execution: { story_timeout: 15, pause_between_stories: 0 },
        completion_detection: { threshold: 0.4, idle_timeout: 5 },
        error_handling: { strategy: 'skip', max_retries: 0 }
      }
    }
    
    if (presets[preset]) {
      this.settings = { ...this.defaultSettings(), ...presets[preset] }
      this.updateFormFields()
      this.updatePreview()
    }
  }

  updateFormFields() {
    // Update all form fields with current settings
    this.element.querySelectorAll('[data-setting]').forEach(field => {
      const path = field.dataset.setting.split('.')
      let value = this.settings
      for (const key of path) {
        value = value[key]
      }
      
      if (field.type === 'checkbox') {
        field.checked = value
      } else {
        field.value = value
      }
    })
    
    // Update threshold display
    if (this.hasThresholdValueTarget) {
      const threshold = this.settings.completion_detection.threshold
      this.thresholdValueTarget.textContent = `${Math.round(threshold * 100)}%`
    }
  }

  updateThresholdDisplay(event) {
    const value = parseFloat(event.target.value)
    this.thresholdValueTarget.textContent = `${Math.round(value * 100)}%`
    this.updateSetting('completion_detection.threshold', value)
  }

  addPattern(event) {
    event.preventDefault()
    
    const patternInput = document.createElement('div')
    patternInput.className = 'input-group'
    patternInput.innerHTML = `
      <input type="text" 
             class="input input-bordered input-sm flex-1" 
             placeholder="e.g., /Task completed|Story done/" />
      <button class="btn btn-sm btn-error" data-action="click->automation-settings#removePattern">
        <i class="fas fa-trash"></i>
      </button>
    `
    
    this.patternListTarget.appendChild(patternInput)
  }

  removePattern(event) {
    event.preventDefault()
    event.currentTarget.closest('.input-group').remove()
    this.savePatterns()
  }

  savePatterns() {
    const patterns = []
    this.patternListTarget.querySelectorAll('input[type="text"]').forEach(input => {
      if (input.value.trim()) {
        patterns.push(input.value.trim())
      }
    })
    this.updateSetting('completion_detection.custom_patterns', patterns)
  }

  updateSetting(path, value) {
    const keys = path.split('.')
    let obj = this.settings
    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]]
    }
    obj[keys[keys.length - 1]] = value
    this.updatePreview()
  }

  updatePreview() {
    // Calculate estimated times based on settings
    const storyTimeout = this.settings.execution.story_timeout
    const pauseBetween = this.settings.execution.pause_between_stories
    const avgStoryTime = Math.round(storyTimeout * 0.6) // Assume 60% of timeout on average
    
    // Assuming 30 stories in queue
    const queueSize = 30
    const totalTime = (avgStoryTime * queueSize) + (pauseBetween * (queueSize - 1) / 60)
    
    // Calculate reliability score based on settings
    const threshold = this.settings.completion_detection.threshold
    const retries = this.settings.error_handling.max_retries
    const strategy = this.settings.error_handling.strategy
    
    let reliability = 50
    reliability += threshold * 30 // Higher threshold = more reliable
    reliability += retries * 10 // More retries = more reliable
    if (strategy === 'stop') reliability += 10
    if (strategy === 'retry') reliability += 5
    if (this.settings.error_handling.auto_recovery) reliability += 5
    
    // Update preview displays
    if (this.hasAvgTimePreviewTarget) {
      this.avgTimePreviewTarget.textContent = `~${avgStoryTime} min`
    }
    if (this.hasQueueTimePreviewTarget) {
      const hours = Math.floor(totalTime / 60)
      const minutes = Math.round(totalTime % 60)
      this.queueTimePreviewTarget.textContent = hours > 0 ? `~${hours}h ${minutes}m` : `~${minutes} min`
    }
    if (this.hasReliabilityPreviewTarget) {
      this.reliabilityPreviewTarget.textContent = `${Math.min(100, Math.round(reliability))}%`
    }
  }

  async saveSettings(event) {
    event.preventDefault()
    
    // Collect all settings from form
    this.element.querySelectorAll('[data-setting]').forEach(field => {
      const path = field.dataset.setting.split('.')
      let value
      
      if (field.type === 'checkbox') {
        value = field.checked
      } else if (field.type === 'number' || field.type === 'range') {
        value = parseFloat(field.value)
      } else {
        value = field.value
      }
      
      this.updateSetting(field.dataset.setting, value)
    })
    
    // Save patterns
    this.savePatterns()
    
    // Save to localStorage
    localStorage.setItem(`automation_settings_${this.buildSessionId}`, JSON.stringify(this.settings))
    
    // Save to server
    try {
      const response = await fetch(`/app_projects/${this.buildSessionId}/build_sessions/${this.buildSessionId}/update_automation_settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ settings: this.settings })
      })
      
      if (response.ok) {
        this.showNotification('Settings saved successfully', 'success')
        // Close modal
        document.getElementById('automation-settings-modal').close()
      } else {
        throw new Error('Failed to save settings')
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      this.showNotification('Failed to save settings', 'error')
    }
  }

  importSettings(event) {
    event.preventDefault()
    
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        const text = await file.text()
        try {
          const imported = JSON.parse(text)
          this.settings = { ...this.defaultSettings(), ...imported }
          this.updateFormFields()
          this.updatePreview()
          this.showNotification('Settings imported successfully', 'success')
        } catch (error) {
          this.showNotification('Invalid settings file', 'error')
        }
      }
    }
    input.click()
  }

  exportSettings(event) {
    event.preventDefault()
    
    const dataStr = JSON.stringify(this.settings, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    
    const exportFileDefaultName = `automation-settings-${new Date().toISOString().split('T')[0]}.json`
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  showNotification(message, type) {
    // Create toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed bottom-4 right-4 z-50 max-w-sm`
    toast.innerHTML = `
      <span>${message}</span>
    `
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }
}